import React, { useState, memo, useRef, use } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowBack, Edit, Delete, CameraAlt } from "@mui/icons-material";
import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Toolbar,
  IconButton,
  Typography,
  Autocomplete,
  Grid,
  Snackbar,
  Alert
} from "@mui/material";
import 'react-quill/dist/quill.snow.css';
import useUserData from "../hooks/useUserData";
import logo from "../Assets/OWWLogo.png";
import UpdateUserPassword from "../components/UpdateUserPassword";
import { gql, useMutation } from "@apollo/client";
import LoadingPage from "./LoadingPage";
import BackWithBreadcrumbAppbar from "../components/BackWithBreadcrumbAppbar";
import CustomModal from "../components/CustomModal";
import { useTranslation } from "react-i18next";
import { UPLOAD_IMAGES_API_URL } from "../constant";
import countries from "../countries.json";
import MDEditor from '@uiw/react-md-editor';

const UPDATE_USER_DATA = gql`
  mutation updateUser($id: ID!, $userData: String!) {
    updateUser(id: $id, userData: $userData)
  }
`;

const Profile = memo(() => {
  const { t } = useTranslation();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editable, setEditable] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const navigate = useNavigate();
  const { data, refetch } = useUserData();
  const fileInputRef = useRef(null);

  let userdata = data?.me;
  if (typeof (data?.me) === "string") (userdata = JSON.parse(data?.me));

  const [editData, setEditData] = useState({
    name: userdata?.name,
    email: userdata?.email,
    company: userdata?.company,
    designation: userdata?.designation,
    bio: userdata?.bio,
    stallNo: userdata?.stallNo,
    profile_pic: userdata?.profile_pic,
    country: userdata?.country,
    company_name: userdata?.company_name,
    website: userdata?.website,
    fascia_board: userdata?.fascia_board,
    brands: userdata?.brands,
    company_profile: userdata?.company_profile,
    sponsorship_level: userdata?.sponsorship_level,
    linkedIn: userdata?.linkedIn,
  });

  const handleInitiseEdit = () => {
    setEditable(!editable);
    if (!editable) {
      setEditData({
        name: userdata?.name,
        email: userdata?.email,
        company: userdata?.company,
        designation: userdata?.designation,
        bio: userdata?.bio,
        stallNo: userdata?.stallNo,
        profile_pic: userdata?.profile_pic,
        country: userdata?.country,
        company_name: userdata?.company_name,
        website: userdata?.website,
        fascia_board: userdata?.fascia_board,
        brands: userdata?.brands,
        company_profile: userdata?.company_profile,
        sponsorship_level: userdata?.sponsorship_level,
        linkedIn: userdata?.linkedIn,
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone" && isNaN(value)) return;
    setEditData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBioChange = (value) => {
    setEditData(prev => ({ ...prev, bio: value }));
  };

  const handleCompanyProfileChange = (value) => {
    setEditData(prev => ({ ...prev, company_profile: value }));
  };

  const [updateUser, { loading }] = useMutation(UPDATE_USER_DATA);

  const handleUpdateProfile = async () => {
    let newUserData;
    if (userdata?.role !== "exhibitor" || userdata.role !== "speaker" || userdata?.role !== "exhibitors"|| userdata?.role !== "sponsors") {
        const { bio, stallNo,fascia_board,website,brands,company_profile,sponsorship_level,linkedIn, ...newUsersData } = editData;
      newUserData = newUsersData;
        
    }
    if (userdata?.role === "speaker") {
      const { fascia_board,stallNo,website,brands,company_profile,sponsorship_level, ...newUsersData } = editData;
      newUserData = newUsersData;
    }
    if (userdata?.role === "exhibitor") {
       const { fascia_board, website,brands,company_name,company_profile,linkedIn,sponsorship_level, ...newUsersData } = editData;
       newUserData = newUsersData;
      
    }
    if (userdata?.role === "exhibitors") {
      newUserData = {
        company_name: editData.company_name,
        email: editData.email,
        country: editData.country,
        website: editData.website,
        stallNo: editData.stallNo,
        fascia_board: editData.fascia_board,
        brands: editData.brands,
        profile_pic: editData.profile_pic,
        company_profile: editData.company_profile,
      };
    }
    if (userdata?.role === "sponsors") {
      newUserData = {
        company_name: editData.company_name,
        email: editData.email,
        country: editData.country,
        website: editData.website,
        stallNo: editData.stallNo,
        sponsorship_level: editData.sponsorship_level,
        profile_pic: editData.profile_pic,
        company_profile: editData.company_profile,
      };
    }
    const userData = JSON.stringify(newUserData);
    try {
      await updateUser({ variables: { id: userdata.id, userData } });
      setSnackbarMessage("Profile updated successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      refetch();
      setEditable(false);
    } catch (error) {
      setSnackbarMessage(error.message);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      setSnackbarMessage('Only PNG, JPEG, JPG files are allowed');
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setSnackbarMessage('File size must be less than 2MB');
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const formData = new FormData();
    formData.append('image', file);
    setImageLoading(true);

    try {
      const response = await fetch(`${UPLOAD_IMAGES_API_URL}/upload/user/${userdata.eventId}/${userdata.role}/${userdata.id}`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth.app.eventegration.com')}`,
        }
      });

      if (!response.ok) throw new Error('Upload failed');
      const data = await response.json();

      if (data.success) {
        setSnackbarMessage('Profile picture updated');
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        refetch();
      }
    } catch (error) {
      setSnackbarMessage(error.message);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setImageLoading(false);
    }
  };

  const handleRemoveImage = async () => {
    try {
      const userData = JSON.stringify({ ...editData, profile_pic: null });
      await updateUser({ variables: { id: userdata.id, userData } });
      setSnackbarMessage('Profile picture removed');
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      refetch();
    } catch (error) {
      setSnackbarMessage(error.message);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };
const getFieldData = ()=>{
    if(userdata?.role === "exhibitors") return [
        { label: t("profile.fields.companyName"), name: "company_name", type: "text", grid: 6, readOnly: false },
        { label: t("profile.fields.email"), name: "email", type: "email", grid: 6, readOnly: true },
        { label: t("profile.fields.country"), name: "country", type: "select", grid: 6, readOnly: false },
        { label: t("profile.fields.website"), name: "website", type: "text", grid: 6, readOnly: false },
        { label: t("profile.fields.stallNo"), name: "stallNo", type: "text", grid: 6, readOnly: false },
        { label: t("profile.fields.fasciaBoard"), name: "fascia_board", type: "text", grid: 6, readOnly: false },
        { label: t("profile.fields.brands"), name: "brands", type: "text", grid: 12, readOnly: false },
        { label: t("profile.fields.companyProfile"), name: "company_profile", type: "richtext", grid: 12, readOnly: false },
      ] ;
    if(userdata?.role === "sponsors") return [
        { label: t("profile.fields.companyName"), name: "company_name", type: "text", grid: 6, readOnly: false },
        { label: t("profile.fields.email"), name: "email", type: "email", grid: 6, readOnly: true },
        { label: t("profile.fields.country"), name: "country", type: "select", grid: 6, readOnly: false },
        { label: t("profile.fields.website"), name: "website", type: "text", grid: 6, readOnly: false },
        { label: t("profile.fields.stallNo"), name: "stallNo", type: "text", grid: 6, readOnly: false },
        { label: t("profile.fields.sponsorship_level"), name: "sponsorship_level", type: "text", grid: 6, readOnly: true },
        { label: t("profile.fields.companyProfile"), name: "company_profile", type: "richtext", grid: 12, readOnly: false },
      ] ;
      if(userdata?.role === "exhibitor") return[
        { label: t("profile.fields.fullName"), name: "name", type: "text", grid: 6, readOnly: false },
        { label: t("profile.fields.email"), name: "email", type: "email", grid: 6, readOnly: true },
        { label: t("profile.fields.company"), name: "company", type: "text", grid: 6, readOnly: false },
        { label: t("profile.fields.designation"), name: "designation", type: "text", grid: 6, readOnly: false },
        { label: t("profile.fields.country"), name: "country", type: "select", grid: 6, readOnly: false },
        { label: t("profile.fields.stallNo"), name: "stallNo", type: "text", grid: 6, readOnly: false },
        { label: t("profile.fields.bio"), name: "bio", type: "richtext", grid: 12, readOnly: false },
      ]; 
      if(userdata?.role === "speaker") return [
        { label: t("profile.fields.fullName"), name: "name", type: "text", grid: 6, readOnly: false },
        { label: t("profile.fields.email"), name: "email", type: "email", grid: 6, readOnly: true },
        { label: t("profile.fields.company"), name: "company", type: "text", grid: 6, readOnly: false },
        { label: t("profile.fields.designation"), name: "designation", type: "text", grid: 6, readOnly: false },
        { label: t("profile.fields.country"), name: "country", type: "select", grid: 6, readOnly: false },
        { label: t("profile.fields.bio"), name: "bio", type: "richtext", grid: 12, readOnly: false },
        { label: t("profile.fields.linkedIn"), name: "linkedIn", type: "url", grid: 12, readOnly: false },
      ] ;
      else return [
        { label: t("profile.fields.fullName"), name: "name", type: "text", grid: 6, readOnly: false },
        { label: t("profile.fields.email"), name: "email", type: "email", grid: 6, readOnly: true },
        { label: t("profile.fields.company"), name: "company", type: "text", grid: 6, readOnly: false },
        { label: t("profile.fields.designation"), name: "designation", type: "text", grid: 6, readOnly: false },
        { label: t("profile.fields.country"), name: "country", type: "select", grid: 6, readOnly: false },
      ]
}
const fieldData = getFieldData();
  return (
    <>
      <BackWithBreadcrumbAppbar
        links={[{ label: t('dashboard'), href: '/' }]}
        currentPage={t('Profile')}
      />
      {!userdata ? (
        <LoadingPage />
      ) : (
        <Paper sx={{
          p: 3,
          bgcolor: 'background.paper',
          minHeight: 'calc(100vh - 64px)',
          boxShadow: 3,
        }}>
          <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={4}>
            <Box
              flex={1}
              display="flex"
              flexDirection="column"
              alignItems="center"
              px={{ xs: 0, md: 2 }}
              py={{ xs: 3, md: 2 }}
            >
              <Box sx={{
                position: 'relative',
                width: 150,
                height: 150,
                borderRadius: '50%',
                overflow: 'hidden',
                transition: 'transform 0.3s',
                '&:hover': {
                  transform: 'scale(1.05)',
                  '& .overlay': { opacity: 1 }
                }
              }}>
                <img
                  src={
                    userdata.profile_pic
                      ? userdata?.profile_pic
                      : './profile_pic.png'
                  }
                  alt="Profile"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: userdata.profile_pic ? 'none' : 'grayscale(100%)'
                  }}
                />
                {imageLoading && (
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(0,0,0,0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CircularProgress color="secondary" />
                  </Box>
                )}
                <Box
                  className="overlay"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(0,0,0,0.5)',
                    opacity: 0,
                    transition: 'opacity 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <IconButton
                    color="primary"
                    onClick={() => fileInputRef.current.click()}
                  >
                    <CameraAlt sx={{ color: 'white' }} />
                  </IconButton>
                  {userdata.profile_pic && (
                    <IconButton color="error" onClick={handleRemoveImage}>
                      <Delete sx={{ color: 'white' }} />
                    </IconButton>
                  )}
                </Box>
              </Box>
              <input
                type="file"
                ref={fileInputRef}
                hidden
                accept="image/png, image/jpeg, image/jpg"
                onChange={(e) => handleImageUpload(e.target.files[0])}
              />
              <Typography variant="subtitle2" sx={{ mt: 2, color: 'text.secondary' }}>
                {t('profile.imageRequirements')}
              </Typography>
              <Box mt={2} display="flex" gap={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => setShowPasswordModal(true)}
                  fullWidth={{ xs: true, sm: false }}
                >
                  {t('profile.buttons.updatePassword')}
                </Button>
                <Button
                  variant="contained"
                  color={editable ? "error" : "primary"}
                  onClick={handleInitiseEdit}
                  fullWidth={{ xs: true, sm: false }}
                >
                  {editable ? t('profile.buttons.cancel') : t('profile.buttons.editProfile')}
                </Button>
              </Box>
            </Box>

            <Box flex={2} py={{ xs: 2, md: 2 }} px={{ xs: 0, md: 2 }}>
              <Grid container spacing={3}>
                {fieldData.map((field) => (
                  <Grid item xs={12} md={field.grid} key={field.name}>
                    {field.type === "select" ? (
                      <Autocomplete
                        options={countries}
                        value={editable ? editData[field.name] : userdata[field.name]}
                        disabled={!editable}
                        onChange={(event, value) => {
                          setEditData((prev) => ({ ...prev, [field.name]: value?.label }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={field.label}
                            variant={editable ? "filled" : "outlined"}
                            sx={{
                              '& .MuiFilledInput-root': {
                                bgcolor: 'background.default',
                              }
                            }}
                          />
                        )}
                      />
                    ) : field.type === "richtext" ? (
                      <Box>
                        <Typography variant="caption" color="textSecondary" gutterBottom>
                          {field.label}
                        </Typography>
                        {editable ? (
                          <Box sx={{
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            overflow: 'hidden'
                          }}>
                            {field.name === "bio" ? (
                              <MDEditor
                                value={editData.bio || ''}
                                onChange={handleBioChange}
                                preview="edit"
                                height={200}
                                visibleDragbar={false}
                              />
                            ) : (
                              <MDEditor
                                value={editData.company_profile || ''}
                                onChange={handleCompanyProfileChange}
                                preview="edit"
                                height={200}
                                visibleDragbar={false}
                              />
                            )}
                          </Box>
                        ) : (
                          <MDEditor.Markdown
                            source={field.name === "bio" ? userdata.bio : userdata.company_profile}
                            style={{
                              padding: 16,
                              backgroundColor: 'background.default',
                              borderRadius: 4
                            }}
                          />
                        )}
                      </Box>
                    ) : (
                      <TextField
                        fullWidth
                        label={field.label}
                        type={field.type}
                        readOnly={field.readOnly}
                        value={editable ? editData[field.name] : userdata[field.name]}
                        variant={editable ? "filled" : "outlined"}
                        disabled={!editable || field.readOnly}
                        name={field.name}
                        onChange={handleInputChange}
                        sx={{
                          '& .MuiFilledInput-root': {
                            bgcolor: 'background.default',
                          }
                        }}
                      />
                    )}
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
          {editable && (
            <Box display="flex" justifyContent="flex-end" mt={3}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpdateProfile}
                disabled={loading}
                sx={{ minWidth: 120 }}
              >
                {loading ? <CircularProgress size={24} /> : t('profile.buttons.saveChanges')}
              </Button>
            </Box>
          )}
        </Paper>
      )}

      <CustomModal
        open={showPasswordModal}
        handleClose={() => setShowPasswordModal(false)}
      >
        <UpdateUserPassword id={userdata?.id} role={userdata?.role} />
      </CustomModal>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
});

export default Profile;
